/**
 * GitHub account linking via the OAuth Device Flow.
 *
 * Device Flow needs NO callback URL, NO local port, and NO client secret — so it
 * can't conflict with a dev's local servers and needs no backend endpoint. The
 * app shows the user a short code, opens github.com/login/device, and polls for
 * the token. Requests go through the Tauri HTTP adapter (no CORS).
 *
 * Requires the OAuth App to have "Device Flow" enabled and REACT_APP_GITHUB_CLIENT_ID set.
 */
import axios from 'axios';
import { isTauri } from '../utils/tauri';
import { tauriAxiosAdapter } from '../utils/tauriAxiosAdapter';
import {
  getGitHubAccount,
  setGitHubAccount,
  clearGitHubAccount,
} from './accountsStore';

const GITHUB_CLIENT_ID = process.env.REACT_APP_GITHUB_CLIENT_ID || '';
// `repo` scope allows creating/reading/writing the user's repositories;
// `read:org` lists organisations whose membership is private, so the repo
// picker can show their repositories (28.10.2026 — GitHub Desktop and VS
// Code ask for it too).
const GITHUB_SCOPE = 'repo read:org';
// Sign-in uses the same scopes plus identity scopes, so a single
// authorization both signs the user into Apiboo AND yields a git-ready linked
// account. `user:email` lets the backend read the verified email for the token.
const GITHUB_LOGIN_SCOPE = 'repo read:org read:user user:email';

export { getGitHubAccount };

export function isGitHubClientConfigured() {
  return Boolean(GITHUB_CLIENT_ID);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/** github.com endpoints (device code + token poll) — form-encoded, JSON response. */
function githubWeb() {
  return axios.create({
    baseURL: 'https://github.com',
    adapter: tauriAxiosAdapter,
    timeout: 15000,
    // Don't throw on 4xx — the token endpoint returns errors as 200/4xx with a body we parse.
    validateStatus: () => true,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/x-www-form-urlencoded',
    },
  });
}

async function requestDeviceCode(scope = GITHUB_SCOPE) {
  const body = new URLSearchParams({
    client_id: GITHUB_CLIENT_ID,
    scope,
  }).toString();
  const { data } = await githubWeb().post('/login/device/code', body);
  if (!data?.device_code) {
    throw new Error(data?.error_description || 'Could not start GitHub authorization');
  }
  return data; // { device_code, user_code, verification_uri, expires_in, interval }
}

async function pollForToken(deviceCode, interval, expiresIn, shouldCancel) {
  const deadline = Date.now() + (expiresIn || 900) * 1000;
  let waitMs = Math.max(interval || 5, 5) * 1000;

  while (Date.now() < deadline) {
    if (shouldCancel?.()) throw new Error('CANCELLED');
    await sleep(waitMs);
    if (shouldCancel?.()) throw new Error('CANCELLED');

    const body = new URLSearchParams({
      client_id: GITHUB_CLIENT_ID,
      device_code: deviceCode,
      grant_type: 'urn:ietf:params:oauth:grant-type:device_code',
    }).toString();
    const { data } = await githubWeb().post('/login/oauth/access_token', body);

    if (data?.access_token) return data.access_token;
    switch (data?.error) {
      case 'authorization_pending':
        break; // keep waiting
      case 'slow_down':
        waitMs += 5000;
        break;
      case 'expired_token':
        throw new Error('The code expired before you approved it. Please try again.');
      case 'access_denied':
        throw new Error('Authorization was denied.');
      default:
        throw new Error(data?.error_description || data?.error || 'GitHub authorization failed');
    }
  }
  throw new Error('Timed out waiting for GitHub authorization.');
}

async function fetchUser(token) {
  const gh = axios.create({
    baseURL: 'https://api.github.com',
    adapter: tauriAxiosAdapter,
    timeout: 15000,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/vnd.github+json',
      'X-GitHub-Api-Version': '2022-11-28',
      'User-Agent': 'Apiboo',
    },
  });
  const { data } = await gh.get('/user');
  return data || {};
}

/**
 * Run the GitHub Device Flow for the given scope and return the access token.
 * Shared by both account linking (repo scope) and Apiboo sign-in (identity scope).
 *
 * @param {object} opts
 * @param {string} opts.scope - GitHub OAuth scope(s) to request
 * @param {(userCode:string, verificationUri:string)=>void} [opts.onCode] - called once the code is ready
 * @param {()=>boolean} [opts.shouldCancel] - polled to allow cancellation
 * @returns {Promise<string>} the GitHub access token
 */
async function runDeviceFlow({ scope, onCode, shouldCancel }) {
  if (!isTauri()) {
    throw new Error('GitHub authorization requires Apiboo Desktop');
  }
  if (!GITHUB_CLIENT_ID) {
    throw new Error('GitHub client ID is not configured (REACT_APP_GITHUB_CLIENT_ID)');
  }

  const device = await requestDeviceCode(scope);
  onCode?.(device.user_code, device.verification_uri);

  // Open the verification page so the user can paste the code.
  try {
    const { open } = await import('@tauri-apps/plugin-shell');
    await open(device.verification_uri);
  } catch {
    // user can still open it manually from the shown link
  }

  return pollForToken(
    device.device_code,
    device.interval,
    device.expires_in,
    shouldCancel
  );
}

/** Shape the persisted linked-account record from a token + GitHub profile. */
function buildAccount(token, user) {
  return {
    token,
    login: user.login || '',
    avatarUrl: user.avatar_url || '',
    linkedAt: Date.now(),
  };
}

/**
 * Sign in to Apiboo using GitHub as the identity provider.
 *
 * Runs the Device Flow (repo + identity scope) and returns the raw GitHub token
 * plus a ready-to-persist linked-account record. The caller exchanges the token
 * with the Apiboo backend (`POST /auth/github`) for a session and — once the
 * backend confirms the identity — persists the account via `setGitHubAccount`
 * so it shows in Settings and is usable for git sync.
 *
 * @param {object} opts
 * @param {(userCode:string, verificationUri:string)=>void} [opts.onCode]
 * @param {()=>boolean} [opts.shouldCancel]
 * @returns {Promise<{token:string, account:object}>}
 */
export async function signInWithGitHubDevice({ onCode, shouldCancel } = {}) {
  const token = await runDeviceFlow({ scope: GITHUB_LOGIN_SCOPE, onCode, shouldCancel });
  const user = await fetchUser(token);
  return { token, account: buildAccount(token, user) };
}

/**
 * Run the GitHub Device Flow and persist the linked account.
 *
 * @param {object} opts
 * @param {(userCode:string, verificationUri:string)=>void} [opts.onCode] - called once the code is ready
 * @param {()=>boolean} [opts.shouldCancel] - polled to allow cancellation
 * @returns {Promise<{login:string, avatarUrl:string, connected:true}>}
 */
export async function linkGitHubDevice({ onCode, shouldCancel } = {}) {
  const token = await runDeviceFlow({ scope: GITHUB_SCOPE, onCode, shouldCancel });
  const user = await fetchUser(token);

  const account = buildAccount(token, user);
  await setGitHubAccount(account);

  return { login: account.login, avatarUrl: account.avatarUrl, connected: true };
}

/**
 * Persist a linked GitHub account from an already-obtained access token.
 * Used by the OAuth web flow, where the backend obtained the (repo-scoped) token
 * and handed it back to the app. Fetches the profile for display, then stores it.
 */
export async function linkGitHubFromToken(token) {
  if (!token) return null;
  const user = await fetchUser(token);
  const account = buildAccount(token, user);
  await setGitHubAccount(account);
  return account;
}

export async function unlinkGitHub() {
  // Device-flow tokens can't be revoked from the client (revocation needs the
  // app secret); just drop it locally. The user can revoke app access at
  // github.com/settings/applications if desired.
  await clearGitHubAccount();
}

/** Lightweight connection state for UI. */
export async function getGitHubConnection() {
  const account = await getGitHubAccount();
  return {
    connected: Boolean(account?.token),
    login: account?.login || '',
    avatarUrl: account?.avatarUrl || '',
  };
}

/** The raw token for git operations. Returns '' when not linked. */
export async function getGitHubToken() {
  const account = await getGitHubAccount();
  return account?.token || '';
}
